'use client';

import { variables } from './variables';
import { createTheme } from '@mui/material/styles';
import { createBreakpoints } from '@mui/system';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';
import sharedTheme from '../shared/theme';

const { colors, fonts, borderRadius, padding, typography } = variables;

const breakpoints = createBreakpoints({});

const defaultTheme = sharedTheme(variables);

const outlinedInputStyles = {
    styleOverrides: {
        root: {
            ...defaultTheme.MuiOutlinedInput.styleOverrides.root,
            height: '66px',
            borderRadius: '8px',

            '& input': {
                padding: '20px',
                height: '26px',
                '&::placeholder': {
                    color: variables.colors.palette.neutral['700'],
                    opacity: 1,
                },
            },

            '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                borderColor: variables.colors.other.inputs.default,
                borderRadius: '8px',
                transition: 'border-color 0.15s ease-in-out',
            },

            '&:hover .MuiOutlinedInput-notchedOutline, &:hover .MuiPickersOutlinedInput-notchedOutline':
                {
                    borderColor: 'rgba(151, 203, 212, 1)',
                },

            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: variables.colors.palette.primary['300'],
                    borderWidth: '1px',
                },
            },

            '&.Mui-disabled': {
                backgroundColor: variables.colors.palette.neutral[300],
            },
        },

        notchedOutline: {
            borderRadius: '8px',
        },
    },
};

export const theme = createTheme({
    colors,
    fonts,
    borderRadius,
    padding,
    palette: colors.palette,
    typography,
    components: {
        MuiContainer: {
            styleOverrides: {
                root: {
                    '&.MuiContainer-maxWidthLg': {
                        margin: 'auto',
                    },
                    '&.staticPage': {
                        padding: '2rem',
                        marginBottom: '2rem',
                        [breakpoints.up('sm')]: {
                            padding: '4rem',
                        },
                        '> h1': {
                            marginBottom: '2rem',
                        },
                        '> h2': {
                            fontFamily: variables.typography.fontFamily,
                            color: variables.colors.palette.secondary['500'],
                        },
                        '> p': {
                            fontFamily: variables.typography.fontFamily,
                            color: variables.colors.palette.neutral['900'],
                        },
                    },
                    '.selectOpinionAccessDeniedAlert': {
                        marginTop: '8rem',
                        backgroundColor: addOpacityToThemeColor(
                            variables.colors.palette.warning.main,
                            0.1,
                        ),
                        padding: '2rem',
                        borderRadius: '8px',
                    },
                    '.faqQuestionHeader': {
                        padding: '1.5rem',
                        ':hover': {
                            backgroundColor: variables.colors.palette.neutral['200'],
                        },
                    },
                    '.faqQuestionTitle': {
                        fontSize: '1.125rem',
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: variables.colors.palette.neutral['100'],
                    color: variables.colors.palette.neutral['800'],
                    border: 'none',
                    boxShadow: 'none',
                    borderRadius: '8px',
                    padding: '16px',
                    margin: '14px 0',
                    '&:before': {
                        display: 'none',
                    },
                    '&:first-of-type, &:last-of-type': {
                        borderRadius: '8px',
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                standardError: {
                    color: variables.colors.error.main,
                },
                standardInfo: {
                    color: variables.colors.palette.info.main,
                },
                root: {
                    '& a': {
                        color: variables.colors.links.main,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    '& .MuiAccordionSummary-expandIconWrapper': {
                        color: variables.colors.palette.neutral['900'],
                    },
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                        color: variables.colors.palette.neutral['900'],
                    },
                    '& .MuiSvgIcon-root': {
                        color: variables.colors.palette.neutral['900'],
                    },
                    '& .MuiTypography-root': {
                        fontWeight: 400,
                        color: variables.colors.palette.neutral['900'],

                        '.select-opinion-faq &.Mui-expanded, .select-opinion-faq &[color="primary"]':
                            {
                                color: variables.colors.palette.neutral['900'],
                                fontWeight: 400,
                            },
                    },
                },
                content: {
                    margin: 0,
                    padding: 0,
                    '&.Mui-expanded': {
                        margin: 0,
                        '& .MuiTypography-root': {
                            fontWeight: 400,
                        },
                        '.select-opinion-faq & .MuiTypography-root': {
                            color: variables.colors.palette.neutral['900'],
                            fontWeight: 400,
                        },
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: 0,
                    '& .MuiTypography-root': {
                        color: variables.colors.palette.neutral['900'],
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '8px',
                    minHeight: '40px',
                    padding: '10px 24px',
                    fontWeight: 700,
                },
                contained: {
                    color: 'white',
                    '&:hover': {
                        backgroundColor: variables.colors.palette.primary['400'],
                        color: 'white',
                    },
                    '&:active': {
                        backgroundColor: variables.colors.palette.primary['600'],
                        color: 'white',
                    },
                    '& a, & [role="link"], & a:hover, & [role="link"]:hover': {
                        color: 'white',
                        backgroundColor: variables.colors.palette.primary['400'],
                    },
                },
                containedSecondary: {
                    color: 'white',
                    backgroundColor: variables.colors.palette.secondary['500'],
                    '&:hover': {
                        backgroundColor: variables.colors.palette.secondary['400'],
                        color: 'white',
                    },
                    '&:active': {
                        backgroundColor: variables.colors.palette.secondary['600'],
                        color: 'white',
                    },
                },
                outlined: {
                    color: variables.colors.palette.primary['500'],
                    backgroundColor: 'white',
                    '&:hover': {
                        color: variables.colors.palette.primary['400'],
                        backgroundColor: variables.colors.palette.primary['100'],
                    },
                    '&:active': {
                        color: variables.colors.palette.primary['600'],
                        backgroundColor: variables.colors.palette.primary['200'],
                    },
                },
                outlinedSecondary: {
                    color: variables.colors.palette.secondary['500'],
                    backgroundColor: 'white',
                    borderColor: variables.colors.palette.secondary['500'],
                    '&:hover': {
                        color: variables.colors.palette.secondary['400'],
                        backgroundColor: variables.colors.palette.secondary['100'],
                        borderColor: variables.colors.palette.secondary['400'],
                    },
                    '&:active': {
                        color: variables.colors.palette.secondary['600'],
                        backgroundColor: variables.colors.palette.secondary['200'],
                        borderColor: variables.colors.palette.secondary['600'],
                    },
                },
                textSecondary: {
                    color: variables.colors.palette.secondary['500'],
                    '&:hover': {
                        color: variables.colors.palette.secondary['400'],
                    },
                    '&:active': {
                        color: variables.colors.palette.secondary['600'],
                    },
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                colorPrimary: {
                    color: variables.colors.palette.neutral['700'],
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiInputLabel.styleOverrides.root,
                    transform: 'translate(20px, 22px) scale(1)',
                    color: variables.colors.palette.neutral['700'],
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(2px, -20px) scale(0.75)',
                        zIndex: 1,
                        backgroundColor: 'white',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                    },
                },
            },
        },
        MuiOutlinedInput: outlinedInputStyles,
        MuiPickersOutlinedInput: outlinedInputStyles,
        MuiTextField: {
            styleOverrides: {
                root: {
                    '&.hourlyRate': {
                        width: '100%',
                        [breakpoints.up('md')]: {
                            width: '180px',
                        },
                        '& .MuiInputBase-root': {
                            paddingLeft: '0',
                        },
                    },
                    '.knowledgeSummaryTextArea': {
                        height: 'auto',
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    '&.currencyCode': {
                        backgroundColor: variables.colors.palette.neutral[100],
                        padding: '32px 16px',
                        marginRight: '0',
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: '6px',
                    '&.Mui-checked': {
                        color: variables.colors.palette.secondary['500'],
                    },
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                },
            },
        },
        MuiFormHelperText: defaultTheme.MuiFormHelperText,
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    '& .phrase-link': {
                        color: variables.colors.palette.tertiary['500'],
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                            color: variables.colors.palette.tertiary['400'],
                        },
                        '&:active': {
                            color: variables.colors.palette.tertiary['600'],
                        },
                    },
                },
                asterisk: {
                    color: variables.colors.palette.error.main,
                },
            },
        },
    },
});
